import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../components/AuthContext/AuthContext";
import ReusableAccordion from "../../stories/Accordions/ReusableAccordion";
import VitalityDuplicatedTable from "../../stories/Tables/VitalityDuplicatedTable";
import PageLayout from "../../components/Layout/PageLayout";
import {
  vitalitySearchFormFields,
  getScreeningQuestions,
  bodyMeasurementsColumns,
  bloodPressureColumns,
  lipidProfileColumns,
  glucoseColumns,
  getEnabledSections,
  MEASUREMENT_SECTIONS,
} from "../../stories/Forms/fields";
import LoggedInLayout from "../../stories/LoggedInScreens/LoggedInLayout";
import Notes from "../../stories/Forms/Notes";
import { Button } from "../../stories/Buttons/Button";
import { useLocation, useNavigate } from "react-router";
import VHConfirmationPopup from "../../stories/Modals/VHConfirmationPopup";
import { useConsultationDraft } from "../../hooks/useConsultationDraft";
import { XCircleFill } from "react-bootstrap-icons";
import Spinner from "react-bootstrap/Spinner";
import { addIds, getRangeError } from "../../utils/utils";
import DetailCard from "../../stories/Cards/DetailCard";
import InformationIcon from "../../assets/icons/Information-fill.svg";
import InfoDefaultIcon from "../../assets/icons/Information-default.svg";
import WarningIcon from "../../assets/icons/Warning-default.svg";
import EditIcon from "../../assets/icons/Vitality-edit.svg";
import {
  resultOutcomeGet,
  consultationCompletePost,
  RISK_PROFILE_OPTIONS,
  LIFESTYLE_ADVICE_OPTIONS,
  OUTCOME_OPTIONS,
  CLASSIFICATION_MAP,
} from "../../utils/constants";
import useApi from "../../services/useAPI";
import { Form } from "react-bootstrap";

const OTHER_OPTION_KEY = "other";
const OTHER_OPTION_LABEL_SUFFIX = "(Please include in notes)";

const renderOptionLabel = (option, isChecked) => {
  if (option.key === OTHER_OPTION_KEY && isChecked) {
    return `${option.label} ${OTHER_OPTION_LABEL_SUFFIX}`;
  }
  return option.label;
};

const getClassificationModifier = (result) => {
  if (!result || result === "-" || result === " ") {
    return null;
  }

  const normalized = String(result).toLowerCase().trim();

  if (CLASSIFICATION_MAP[normalized]) {
    return CLASSIFICATION_MAP[normalized];
  }

  const original = String(result).trim();
  return {
    className: "classification-unknown",
    label: original.charAt(0).toUpperCase() + original.slice(1),
  };
};

const renderClassification = (result) => {
  if (!result || result === "-") {
    return "-";
  }

  const classification = getClassificationModifier(result);

  if (!classification) {
    return result;
  }

  return (
    <span className={`classification-chip ${classification.className}`}>
      {classification.label}
    </span>
  );
};

const FIELD_VALIDATION_MAP = {
  bmi: { columns: bodyMeasurementsColumns, dataField: "bmi" },
  waistHeightRatio: {
    columns: bodyMeasurementsColumns,
    dataField: "waistRatio",
  },
  systolicBp: { columns: bloodPressureColumns, dataField: "systolic" },
  diastolicBp: { columns: bloodPressureColumns, dataField: "diastolic" },
  heartRate: { columns: bloodPressureColumns, dataField: "heartRate" },
  totalCholesterol: {
    columns: lipidProfileColumns,
    dataField: "totalCholesterol",
  },
  hdlCholesterol: { columns: lipidProfileColumns, dataField: "hdlCholesterol" },
  triglycerides: { columns: lipidProfileColumns, dataField: "triglycerides" },
  ldlCholesterol: { columns: lipidProfileColumns, dataField: "ldlCholesterol" },
  totalHdlRatio: { columns: lipidProfileColumns, dataField: "totalHdlRatio" },
  nonHdlCholesterol: {
    columns: lipidProfileColumns,
    dataField: "nonHdlCholesterol",
  },
  heartAge: { columns: lipidProfileColumns, dataField: "heartAge" },
  cvdRisk: { columns: lipidProfileColumns, dataField: "cvdRisk" },
  rbg: { columns: glucoseColumns, dataField: "glucose" },
};

const getRiskFieldRangeError = (riskAssessment, apiKey) => {
  const mapping = FIELD_VALIDATION_MAP[apiKey];
  if (!mapping) return null;

  const column = mapping.columns.find((c) => c.dataField === mapping.dataField);
  const { value } = getRiskField(riskAssessment, apiKey);
  return getRangeError(value, column?.min, column?.max);
};

const CLINICAL_TABLE_COLUMNS = [
  { dataField: "parameter", text: "Parameter & Results" },
  {
    dataField: "classification",
    text: "Classification",
    render: renderClassification,
  },
];

const BLOOD_PRESSURE_COLUMNS = [
  { dataField: "reading", text: "Reading" },
  {
    dataField: "systolicDiastolic",
    text: (
      <>
        Systolic/Diastolic <br />
        <span>(mmHg)</span>
      </>
    ),
  },
  {
    dataField: "classification",
    text: "Classification",
    render: renderClassification,
  },
  {
    dataField: "heartRate",
    text: (
      <>
        Heart rate <br />
        <span>(bpm)</span>
      </>
    ),
  },
];

const getRiskField = (riskAssessment, key) =>
  riskAssessment?.[key] ?? { value: null, result: null };

const buildParameterRow = ({
  riskAssessment,
  apiKey,
  label,
  unit,
  isInfo = false,
}) => {
  const field = getRiskField(riskAssessment, apiKey);
  const unitSuffix = unit ? ` (${unit})` : "";
  const hasWarning = Boolean(getRiskFieldRangeError(riskAssessment, apiKey));
  return {
    parameter: (
      <span>
        {label}:<b>{field.value ?? "-"}</b>
        {unitSuffix}
        {isInfo && <img src={InfoDefaultIcon} alt="" />}
        {hasWarning && (
          <img
            src={WarningIcon}
            className="row-warning-icon"
            alt="value outside expected range"
          />
        )}
      </span>
    ),
    classification: field.result || "-",
  };
};

const buildBodyMeasurementsRows = (riskAssessment, rawMeasurements = {}) => {
  return addIds([
    {
      parameter: (
        <span>
          Height: <b>{rawMeasurements.height || "-"}</b> (cm)
        </span>
      ),
      classification: "-",
    },
    {
      parameter: (
        <span>
          Weight: <b>{rawMeasurements.weight || "-"}</b> (kg)
        </span>
      ),
      classification: "-",
    },
    buildParameterRow({
      riskAssessment,
      apiKey: "waistHeightRatio",
      label: "Waist:Height Ratio",
      unit: null,
    }),
    buildParameterRow({
      riskAssessment,
      apiKey: "bmi",
      label: "BMI",
      unit: "kg/m2",
    }),
  ]);
};

const buildBloodPressureRows = (riskAssessment) => {
  const systolic = getRiskField(riskAssessment, "systolicBp");
  const diastolic = getRiskField(riskAssessment, "diastolicBp");
  const heartRate = getRiskField(riskAssessment, "heartRate");

  const systolicError = getRiskFieldRangeError(riskAssessment, "systolicBp");
  const diastolicError = getRiskFieldRangeError(riskAssessment, "diastolicBp");
  const heartRateError = getRiskFieldRangeError(riskAssessment, "heartRate");

  const systolicDiastolic =
    systolic.value != null && diastolic.value != null ? (
      <span>
        <b>
          {systolic.value}/{diastolic.value}
        </b>
        {(systolicError || diastolicError) && (
          <img
            src={WarningIcon}
            className="row-warning-icon"
            alt="value outside expected range"
          />
        )}
      </span>
    ) : (
      <b>-</b>
    );

  const heartRateDisplay = (
    <span>
      {heartRate.value ?? "-"}
      {heartRateError && (
        <img
          src={WarningIcon}
          className="row-warning-icon"
          alt="value outside expected range"
        />
      )}
    </span>
  );

  const classification = systolic.result || diastolic.result || "-";

  return addIds([
    {
      reading: (
        <span>
          Clinic blood
          <br /> pressure
        </span>
      ),
      systolicDiastolic,
      heartRate: heartRateDisplay,
      classification,
    },
  ]);
};

const getHeartRhythmDisplay = (heartRhythmIrregular) => {
   if (heartRhythmIrregular === true) {
    return <span style={{ color: "#D8221F" }}>Yes</span>;
  }
  if (heartRhythmIrregular === false) return "No";
  return "-";
};

const LIPID_PROFILE_FIELD_DEFS = [
  { apiKey: "totalCholesterol", label: "Total Cholesterol", unit: "mmol/L" },
  { apiKey: "hdlCholesterol", label: "HDL Cholesterol", unit: "mmol/L" },
  {
    apiKey: "triglycerides",
    label: "Triglycerides",
    unit: "mmol/L",
    isInfo: true,
  },
  { apiKey: "ldlCholesterol", label: "LDL Cholesterol", unit: "mmol/L" },
  { apiKey: "totalHdlRatio", label: "TC:HDL Ratio", unit: "", isInfo: true },
  { apiKey: "nonHdlCholesterol", label: "Non-HDL Cholesterol", unit: "mmol/L" },
  { apiKey: "heartAge", label: "Heart age", unit: "years" },
  { apiKey: "cvdRisk", label: "10-year CVD risk", unit: "%" },
];

const buildLipidProfileRows = (riskAssessment) =>
  addIds(
    LIPID_PROFILE_FIELD_DEFS.map(({ apiKey, label, unit, isInfo }) =>
      buildParameterRow({ riskAssessment, apiKey, label, unit, isInfo }),
    ),
  );

const buildGlucoseRows = (riskAssessment) =>
  addIds([buildParameterRow({ riskAssessment, apiKey: "rbg", label: "Glucose", unit: "mmol/L" })]);

const getPositiveScreeningQuestions = (questions, answers) =>
  questions.filter((question) => answers[question.name] === true);

const hydrateCheckboxMap = (options, selectedKeys = []) =>
  options.reduce((acc, option) => {
    acc[option.key] = selectedKeys.includes(option.key);
    return acc;
  }, {});

const getSelectedKeys = (checkboxMap) =>
  Object.entries(checkboxMap)
    .filter(([, isChecked]) => isChecked)
    .map(([key]) => key);

const ResultOutcome = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { callApi, serverError, tokenError } = useApi();
  const auth = useAuth();

  const bookingId = location.state?.bookingId;
  const serviceName = location.state?.serviceName;
  const patientSex = location.state?.patientSex;
  const serviceType = location.state?.serviceType;
  const displayUserInfo =
    location.state?.displayUserInfo ?? location.state?.patientDetails ?? auth?.displayUserInfo;
  const service = sessionStorage.getItem("serviceType") || serviceType;

  const {
    draft,
    isLoaded: isDraftLoaded,
    saveSlice,
  } = useConsultationDraft(bookingId, serviceName, patientSex, serviceType);


  const enabledSections = getEnabledSections(serviceName);
  const isSectionEnabled = (section) => enabledSections.includes(section);

  const [riskAssessment, setRiskAssessment] = useState({});
  const [isRiskAssessmentLoaded, setIsRiskAssessmentLoaded] = useState(false);
  const [isPageReady, setIsPageReady] = useState(true);

  useEffect(() => {
    if (!bookingId || !serviceName) return;

    let cancelled = false;

    const fetchResults = async () => {
      try {
        const params = new URLSearchParams({
          booking_id: bookingId,
          service_name: serviceName,
        });

        const response = await callApi(
          null,
          `${resultOutcomeGet}?${params}`,
          "GET",
        );
        if (!cancelled) {
          setRiskAssessment(response?.data || {});
        }
      } catch (error) {
        // Handle error if needed
      } finally {
        if (!cancelled) {
          setIsRiskAssessmentLoaded(true);
        }
      }
    };

    fetchResults();

    return () => {
      cancelled = true;
    };
  }, [bookingId, serviceName]);

  const isLoaded = isDraftLoaded && isRiskAssessmentLoaded;

  const [notes, setNotes] = useState("");
  const [isEditingRiskProfile, setIsEditingRiskProfile] = useState(false);
  const [riskProfile, setRiskProfile] = useState("");
  const [originalRiskProfile, setOriginalRiskProfile] = useState("");
  const notesContainerRef = useRef(null);

  const [lifestyleAdvice, setLifestyleAdvice] = useState({
    healthy_diet: false,
    regular_physical_activity: false,
    reduced_alcohol_intake: false,
    smoking_cessation: false,
    reduced_caffeine_consumption: false,
    reduced_salt_intake: false,
    weight_loss: false,
  });
  const [outcomes, setOutcomes] = useState({
    encourage_continuation: false,
    discuss_result_gp: false,
    lifestyle_advice_given: false,
    further_support: false,
    same_day_referral: false,
    other: false,
  });

  const hasHydrated = useRef(false);
  useEffect(() => {
    if (!isLoaded || hasHydrated.current) return;

    const assessmentOutcome = draft?.assessment_outcome || {};
    const initialRiskProfile =
      riskAssessment?.finalRiskProfile || assessmentOutcome.risk_profile || "";

    setNotes(draft?.notes || "");
    setRiskProfile(initialRiskProfile);
    setOriginalRiskProfile(initialRiskProfile);

    setLifestyleAdvice(
      hydrateCheckboxMap(
        LIFESTYLE_ADVICE_OPTIONS,
        assessmentOutcome.lifestyle_advice,
      ),
    );
    setOutcomes(
      hydrateCheckboxMap(OUTCOME_OPTIONS, assessmentOutcome.outcomes),
    );

    hasHydrated.current = true;
  }, [isLoaded, draft, riskAssessment]);

  const skipNextSave = useRef(true);

  useEffect(() => {
    if (!isLoaded || !hasHydrated.current) return;

    saveSlice({
      assessment_outcome: {
        lifestyle_advice: getSelectedKeys(lifestyleAdvice),
        outcomes: getSelectedKeys(outcomes),
        risk_profile: riskAssessment?.finalRiskProfile,
      },
      notes,
    });
  }, [lifestyleAdvice, outcomes, riskProfile, notes, isLoaded]);

  const rawMeasurements = draft?.clinical_measurements?.body_measurements || {};
  const heartRhythmIrregular =
    draft?.clinical_measurements?.heart_rhythm_irregular;

  const screeningQuestions = getScreeningQuestions(serviceName);
  const screeningAnswers = draft?.screening_questions?.answers || {};

  const focusNotesField = () => {
    const notesField =
      notesContainerRef.current?.querySelector("textarea, input");
    notesField?.focus();
  };

  const handleContinue = async () => {
    setIsPageReady(false);
    const payload = {
      booking_id: bookingId,
      service_name: serviceName,
    };

    const response = await callApi(
      payload,
      consultationCompletePost,
      "POST"
    );

    if (response?.success) {
      setIsPageReady(true);
      navigate("/consultation-success", {
        state: {
          serviceType,
          service: response?.data?.service_type,
          isSuccess: true,
          rejectionMessage: `Consultation completed successfully for booking ID: ${bookingId}`,
        },
        replace: true,
      });
    }
  };

  const handleEditRiskProfile = () => {
    setRiskProfile(originalRiskProfile);
    setIsEditingRiskProfile(true);
  };

  const handleSaveRiskProfile = () => {
    setOriginalRiskProfile(riskProfile);
    setIsEditingRiskProfile(false);
    saveSlice({
      assessment_outcome: {
        risk_profile: riskProfile,
        lifestyle_advice: getSelectedKeys(lifestyleAdvice),
        outcomes: getSelectedKeys(outcomes),
      },
      notes,
    });
  };

  const toggleLifestyleAdvice = (key) => {
    setLifestyleAdvice((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleOutcome = (key) => {
    setOutcomes((prev) => {
      const nextChecked = !prev[key];
      if (key === OTHER_OPTION_KEY && nextChecked) {
        focusNotesField();
      }
      return { ...prev, [key]: nextChecked };
    });
  };

  const handleBack = () => {
    navigate("/patient-record", { replace: true });
  };

  useEffect(() => {
    const isValidAccess = location.state?.fromValidSource === true;
    if (!isValidAccess) {
      navigate("/patient-record", { replace: true });
    }
    if (location.state?.isOutcomePage) {
      const handlePopState = () => {
        navigate("/patient-record", { replace: true });
      };

      window.addEventListener("popstate", handlePopState);

      return () => {
        window.removeEventListener("popstate", handlePopState);
      };
    }
  }, [location, navigate]);

   useEffect(() => {
      if (serverError || tokenError) {
        auth?.showToast(
          "Error",
          tokenError || serverError,
          "../assets/mhc.png",
          {icon: <XCircleFill />,
          className: auth?.featureFlag && "vh-toaster"}
        );
        setIsPageReady(true)
      }
    }, [serverError, tokenError]);

  if (!isLoaded || !isPageReady) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" className="my-custom-spinner" />
      </div>
    );
  }

  return (
    <div className="result-outcome-page">
      <LoggedInLayout isVhlogin={true} vitalityLayout={true}>
        <PageLayout
          heading="Risk profile, advice prompts and outcomes"
          className="clinical-measurement"
          isBack={false}
          onBack={handleBack}
        >
          <div className="d-flex flex-column gap-3">
            {/* Personal details */}
            <div className="outcome-card">
              <DetailCard
                heading="Patient details"
                data={{
                  ...displayUserInfo,
                  booking_id: bookingId,
                }}
                fields={vitalitySearchFormFields}
              />
            </div>

            {/* Risk profile */}
            <div className="outcome-card">
              <div className="outcome-card__header">
                <h1>Risk profile</h1>
                {isEditingRiskProfile ? (
                  <div className="risk-profile-actions">
                    <Button
                      label="Save Changes"
                      onClick={handleSaveRiskProfile}
                      size="medium"
                      variant="primary"
                      disabled={!riskProfile}
                    />
                  </div>
                ) : (
                  <button
                    type="button"
                    className="edit-risk-profile-link"
                    onClick={handleEditRiskProfile}
                  >
                    <img src={EditIcon} alt="edit-icon" />
                    Edit Risk Profile
                  </button>
                )}
              </div>
              <p className="sub-heading">
                Risk profiling must be assessed by the healthcare professional
                and updated if necessary
              </p>

              <div
                className="risk-profile-buttons"
                role="radiogroup"
                aria-label="Risk profile"
              >
                {RISK_PROFILE_OPTIONS.map((option) => (
                  <button
                    type="button"
                    key={option.value}
                    role="radio"
                    aria-checked={riskProfile === option.value}
                    className={`risk-btn${riskProfile === option.value ? " active" : ""}${!isEditingRiskProfile ? " disabled" : ""}`}
                    onClick={() => {
                      if (isEditingRiskProfile) {
                        setRiskProfile(option.value);
                      }
                    }}
                    disabled={!isEditingRiskProfile}
                    style={{
                      cursor: isEditingRiskProfile ? "pointer" : "default",
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Lifestyle prompts */}
            <div className="outcome-card">
              <h1>Lifestyle advice &amp; outcome</h1>

              <div className="lifestyle-columns">
                <div className="lifestyle-column">
                  <p className="lifestyle-column__title">Lifestyle advice</p>
                  {LIFESTYLE_ADVICE_OPTIONS.map((option) => (
                    <Form.Check
                      className="checkbox-item"
                      type="checkbox"
                      key={option.key}
                      id={`lifestyle-${option.key}`}
                      name={option.key}
                      label={option.label}
                      checked={!!lifestyleAdvice?.[option.key]}
                      onChange={() => toggleLifestyleAdvice(option.key)}
                    />
                  ))}
                </div>

                <div className="lifestyle-column">
                  <p className="lifestyle-column__title">Outcome</p>
                  {OUTCOME_OPTIONS.map((option) => {
                    const isChecked = !!outcomes?.[option.key];

                    return (
                      <Form.Check
                        className="checkbox-item"
                        type="checkbox"
                        key={option.key}
                        id={`outcome-${option.key}`} // Connects text click actions to input toggling
                        name={option.key}
                        label={renderOptionLabel(option, isChecked)} // Safely passes your function evaluation
                        checked={isChecked}
                        onChange={() => toggleOutcome(option.key)}
                      />
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Assessment details */}
            <div className="measurement-card">
              <ReusableAccordion sectionTitle="Clinical assessment details">
                <hr />
                <div className="clinical-screening">
                  <p className="clinical-screening__title">
                    Clinical screening
                  </p>
                  {(() => {
                    const positiveQuestions = getPositiveScreeningQuestions(
                      screeningQuestions,
                      screeningAnswers,
                    );
                    if (positiveQuestions.length === 0) {
                      return (
                        <div className="clinical-screening__row">
                          <span>-</span>
                        </div>
                      );
                    }
                    return positiveQuestions.map((question) => (
                      <div
                        className="clinical-screening__row row"
                        key={question.name}
                      >
                        <span className="col-md-9 col-sm-10">
                          {question.label}
                        </span>
                        <span className="col-md-3 col-sm-2">Yes</span>
                      </div>
                    ));
                  })()}
                </div>
                
          {(isSectionEnabled(MEASUREMENT_SECTIONS.BODY_MEASUREMENTS) ||
            isSectionEnabled(MEASUREMENT_SECTIONS.BLOOD_PRESSURE)) && (
            <>
              <hr />
              <div className="clinical-tables-grid">
                {isSectionEnabled(MEASUREMENT_SECTIONS.BODY_MEASUREMENTS) && (
                  <div className="clinical-table-block">
                    <p className="clinical-table-block__title">
                      Body measurements
                    </p>
                    <VitalityDuplicatedTable
                      data={buildBodyMeasurementsRows(
                        riskAssessment,
                        rawMeasurements,
                      )}
                      columns={CLINICAL_TABLE_COLUMNS}
                      pagination={false}
                      onFieldChange={null}
                      errors={{}}
                    />
                  </div>
                )}
                {isSectionEnabled(MEASUREMENT_SECTIONS.BLOOD_PRESSURE) && (
                  <div className="clinical-table-block">
                    <p className="clinical-table-block__title">
                      Blood pressure &amp; pulse
                    </p>
                    <VitalityDuplicatedTable
                      data={buildBloodPressureRows(riskAssessment)}
                      columns={BLOOD_PRESSURE_COLUMNS}
                      pagination={false}
                      onFieldChange={null}
                      errors={{}}
                    />
                    <p className="clinical-table-block__footnote">
                      Irregular pulse detected:{" "}
                      <strong>
                        {getHeartRhythmDisplay(heartRhythmIrregular)}
                      </strong>
                    </p>
                  </div>
                )}
              </div>
            </>
          )}

          {(isSectionEnabled(MEASUREMENT_SECTIONS.LIPID_PROFILE) ||
            isSectionEnabled(MEASUREMENT_SECTIONS.GLUCOSE)) && (
            <>
              <hr />
              <div className="clinical-tables-grid">
                {isSectionEnabled(MEASUREMENT_SECTIONS.LIPID_PROFILE) && (
                  <div className="clinical-table-block">
                    <p className="clinical-table-block__title">
                      Lipid profile (Cholesterol)
                    </p>
                    <VitalityDuplicatedTable
                      data={buildLipidProfileRows(riskAssessment)}
                      columns={CLINICAL_TABLE_COLUMNS}
                      pagination={false}
                      onFieldChange={null}
                      errors={{}}
                    />
                  </div>
                )}
                {(isSectionEnabled(MEASUREMENT_SECTIONS.GLUCOSE) || isSectionEnabled(MEASUREMENT_SECTIONS.LIPID_PROFILE)) && (
                  <div className="clinical-table-block">
                    {isSectionEnabled(MEASUREMENT_SECTIONS.GLUCOSE) && (<>
                    <p className="clinical-table-block__title">
                      Glucose (random, non-fasting)
                    </p>
                    <VitalityDuplicatedTable
                      data={buildGlucoseRows(riskAssessment)}
                      columns={CLINICAL_TABLE_COLUMNS}
                      pagination={false}
                      onFieldChange={null}
                      errors={{}}
                    /></>)}
                    
                    {isSectionEnabled(MEASUREMENT_SECTIONS.LIPID_PROFILE) && (
                    <VHConfirmationPopup
                      infoText={
                        <span>
                          Customer-facing reporting categories have been
                          intentionally simplified to support understanding and
                          engagement. Healthcare professionals should use the
                          result values and overall clinical assessment,
                          alongside the clinical training and Biometrics SOP
                          Appendix, when determining the appropriate advice and
                          referral pathways.
                        </span>
                      }
                      showIcon={false}
                      className={!isSectionEnabled(MEASUREMENT_SECTIONS.GLUCOSE) && "top-margin"}
                    />)}
                  </div>
                )}
              </div>
            </>
          )}
              </ReusableAccordion>
            </div>
          </div>

          {/* Notes */}
          <div ref={notesContainerRef}>
            <Notes
              value={notes}
              onChange={setNotes}
              disabled={false}
              className="mt-4"
              label="Notes"
            />
          </div>
          <div className="button-layout">
            {service !== "Private Service" ?
              <span className="charged-service-chip">
                <img src={InformationIcon} alt="information-icon" />
                {/* Charged Service - {formatCost(serviceType)} */}
                {service}
              </span>
              : <span />
           }
            <Button
              className="mx-2 my-2"
              label="Complete Appointment"
              onClick={handleContinue}
              size="large"
              type="button"
              variant="primary"
            />
          </div>
        </PageLayout>
      </LoggedInLayout>
    </div>
  );
};

export default ResultOutcome;
